import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Star, Heart } from "lucide-react";

const PricingSection = () => {
  const pricingData = [
    {
      icon: "🎂",
      title: "Layer Cake",
      startingPrice: "35 €",
      portions: "10 parts",
      description: "Les classiques revisités avec élégance — parfaits pour anniversaires, mariages et grandes occasions.",
      flavors: [
        "Vanille-Fraise 🍓",
        "Chocolat 🍫", 
        "Mangue-Passion 🥭",
        "Vanille-Coco 🥥",
        "Spéculos-Caramel 🍯",
        "Framboise 🍇",
        "Noisette-Praliné 🌰",
        "Kinder Bueno / Oreo 🍪"
      ],
      features: [
        "Décoration personnalisée",
        "Choix de la saveur",
        "Inscription sur demande",
        "Emballage soigné"
      ],
      popular: true
    },
    {
      icon: "🍓",
      title: "Number Cake",
      startingPrice: "40 €",
      portions: "12 parts",
      description: "Des gâteaux en forme de chiffres, idéals pour marquer un âge ou un événement spécial.",
      flavors: [
        "Mêmes saveurs que les Layer Cakes",
        "Décorés de fleurs fraîches",
        "Macarons colorés",
        "Fruits de saison",
        "Meringues artisanales"
      ],
      features: [
        "Forme de chiffre au choix",
        "Décoration florale",
        "Macarons inclus",
        "Design personnalisé"
      ]
    },
    {
      icon: "🧁",
      title: "Cupcakes",
      startingPrice: "1,50 €",
      portions: "par pièce",
      description: "Petits, mignons et irrésistibles — parfaits pour vos buffets, cadeaux ou sweet tables.",
      flavors: [
        "Toutes les saveurs disponibles",
        "Glaçage crémeux",
        "Décoration dorée",
        "Boîte de 4 minimum"
      ],
      features: [
        "Commande minimum 4 pièces",
        "Emballage individuel",
        "Parfait pour événements",
        "Décoration assortie"
      ]
    },
    {
      icon: "🍮",
      title: "Verrines",
      startingPrice: "2 €",
      portions: "par pièce",
      description: "Douceurs à la cuillère, légères et fondantes pour vos réceptions.",
      flavors: [
        "Crème légère vanille",
        "Chocolat fondant",
        "Fruits rouges",
        "Spéculos maison",
        "Citron meringuée"
      ],
      features: [
        "Lot de 6 minimum",
        "Présentation élégante",
        "Parfait pour buffets",
        "Fraîcheur garantie"
      ]
    },
    {
      icon: "🍱",
      title: "Bento Box Cake",
      startingPrice: "20 €",
      portions: "6 parts",
      description: "Des mini gâteaux individuels, parfaits pour offrir ou se faire plaisir.",
      flavors: [
        "Gâteau rond délicat",
        "Décoration simple",
        "Message personnalisé",
        "Boîte cadeau incluse"
      ],
      features: [
        "Boîte cadeau incluse",
        "Décoration personnalisée",
        "Parfait pour offrir",
        "Taille individuelle"
      ]
    },
    {
      icon: "🎁",
      title: "Bento Cake Individuel",
      startingPrice: "15 €",
      portions: "6 parts",
      description: "Un mini gâteau rond, simple, doux et mignon — parfait pour une personne ou un couple.",
      flavors: [
        "Format mini",
        "Décoration minimaliste",
        "Couleurs pastel",
        "Message court possible"
      ],
      features: [
        "Format individuel",
        "Design minimaliste",
        "Idéal pour couple",
        "Prix accessible"
      ]
    }
  ];

  return (
    <section id="tarifs" className="py-20 bg-gradient-to-b from-pink-50/30 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ✨ Nos gâteaux, nos saveurs, nos prix ✨
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Chaque création est faite sur mesure avec des ingrédients de qualité. 
            Les tarifs varient selon le design, la taille et la complexité de la décoration.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pricingData.map((item, index) => (
            <Card 
              key={index} 
              className={`relative overflow-hidden transition-smooth hover:shadow-elegant ${
                item.popular ? 'border-primary shadow-glow' : 'border-pink-100'
              }`}
            >
              {item.popular && (
                <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 text-sm font-medium">
                  <Star className="w-4 h-4 inline mr-1" />
                  Populaire
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="text-4xl mb-4">{item.icon}</div>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {item.title}
                </CardTitle>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="text-3xl font-bold text-primary">
                    {item.startingPrice}
                  </span>
                  <div className="text-sm text-gray-500">
                    <div>à partir de</div>
                    <Badge variant="secondary" className="mt-1">
                      {item.portions}
                    </Badge>
                  </div>
                </div>
                <CardDescription className="text-gray-600 leading-relaxed">
                  {item.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Flavors */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-primary" />
                    Saveurs disponibles
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {item.flavors.map((flavor, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        {flavor}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Features */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Check className="w-4 h-4 mr-2 text-primary" />
                    Inclus
                  </h4>
                  <ul className="space-y-2">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Check className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  className={`w-full transition-smooth ${
                    item.popular 
                      ? 'gradient-primary text-white shadow-elegant' 
                      : 'border-primary text-primary hover:bg-primary hover:text-white'
                  }`}
                  variant={item.popular ? 'default' : 'outline'}
                  onClick={() => {
                    const element = document.querySelector("#contact");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  Commander maintenant
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <Card className="border-accent/20 bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center">
                <span className="text-2xl mr-3">📍</span>
                Informations pratiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700">
              <div><strong>Atelier :</strong> 3 rue Magenta, 69100 Villeurbanne</div>
              <div><strong>Téléphone / WhatsApp :</strong> +33 7 53 79 76 98</div>
              <div><strong>E-mail :</strong> gemimankdu9@icloud.com</div>
              <div><strong>Instagram :</strong> @sweetsby_gem</div>
            </CardContent>
          </Card>
          
          <Card className="border-primary/20 bg-gradient-to-br from-pink-50 to-rose-50">
            <CardHeader>
              <CardTitle className="text-xl text-gray-900 flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                À savoir
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700">
              <div>• Les tarifs peuvent varier selon la personnalisation</div>
              <div>• Un acompte est demandé pour confirmer la réservation</div>
              <div>• Réservation recommandée 7 jours à l'avance</div>
              <div>• Livraison disponible sur Lyon et alentours</div>
            </CardContent>
          </Card>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-elegant border border-pink-100 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              💬 Demander un devis personnalisé
            </h3>
            <p className="text-gray-600 mb-6">
              Chaque création étant unique, n'hésitez pas à nous contacter pour un devis 
              adapté à vos envies et votre budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="gradient-primary text-white shadow-elegant"
                onClick={() => {
                  const element = document.querySelector("#contact");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                → Demander un devis
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white"
                onClick={() => window.open("https://www.instagram.com/sweetsby_gem", "_blank")}
              >
                Voir nos créations sur Instagram
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;